import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import { useHistory } from 'react-router';
import moment from 'moment';
import { useSelector } from 'react-redux';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpPageLayout, WpPageBody } from 'Wpds/components/page-layout';
import WpButton from 'Wpds/components/button/WpButton';

import * as api from 'Shared/api';
import Spinner from 'Shared/components/Spinner';
import TopNavigation from '../components/TopNavigation/TopNavigation';

import Filter from './Filter';

import MessageTemplateBuilder from './MessageTemplateBuilder';
import ButtonSelector from './ButtonSelector';
import PreviewButton from './PreviewButton';

import './RuleBuilder.scss';

type Filter = {
  property: string;
  operator: string;
  value: string;
  uuid: string;
  parentUuid: string | null;
  incomplete: boolean;
  joinType: string;
};

type RecommendationSetting = {
  name: string;
  data: Array<any>;
};

const emptyFilter = ({ parentUuid = null, joinType = 'OR' } = {}): Filter => ({
  property: null,
  operator: null,
  value: null,
  uuid: uuidv4(),
  incomplete: true,
  joinType,
  parentUuid,
});

export const emptyRecommendationSetting = () => ({
  name: '',
  recommendation_type: 'Recommendation::SalesforceGenericRecordAlert',
  data: [
    { key: 'active', type: 'boolean', label: 'Active', value: false },
    { key: 'alert:email', type: 'boolean', label: 'Alert via email', value: false },
    { key: 'alert:slack', type: 'boolean', label: 'Alert via slack', value: false },
    { key: 'alert:web', type: 'boolean', label: 'Alert via web', value: false },
    { key: 'description', type: 'text', label: 'Description:', value: '' },
    { key: 'message_image', type: 'giphy_url', label: 'GIF to show in messages', value: null },
    { key: 'tags_whitelist', type: 'array:string', label: 'Only send for people with the following tags', value: [] },
    {
      key: 'record_type',
      type: 'select',
      label: 'Alerts are for this record type',
      value: 'Opportunity',
      options: [
        { label: 'Opportunity', value: 'Opportunity' },
        { label: 'Lead', value: 'Lead' },
        { label: 'Contact', value: 'Contact' },
        { label: 'Account', value: 'Account' },
      ],
    },
    {
      key: 'message_template',
      type: 'string',
      value: null,
    },
    { key: 'recommendation_button_settings_ids', type: 'array:integer', label: 'Buttons', value: [] },
    {
      key: 'recipient_field',
      type: 'string',
      label: 'Send to users with this relation to the record',
      value: 'OwnerId',
    },
    { key: 'custom_rule', type: 'string', value: null },
    { key: 'custom_rule_representation', type: 'string', value: [emptyFilter()] },
  ],
});

const filterToObject = ({ property, operator, value }) => {
  let value_sanitized = value;
  if (operator === 'IsLessThan' || operator === 'IsGreaterThan') {
    value_sanitized = `${moment(value).format('YYYYMMDD')}.Date`;
  } else if (operator === 'IsEqForDouble') {
    value_sanitized += '.Double';
  }
  return [operator, property, value_sanitized].filter(a => a);
};

const filtersToExpression = ({ filters, parentUuid = null }) => {
  if (filters.length === 0) {
    return [];
  }

  return [
    filters[0].joinType,
    ...filters
      .filter(f => f.parentUuid === parentUuid)
      .map(filter => {
        const children = filters.filter(f => f.parentUuid === filter.uuid);
        if (children.length > 0) {
          const nested = filtersToExpression({ filters: children, parentUuid: filter.uuid });
          nested.splice(1, 0, filterToObject(filter));
          return nested;
        }
        return filterToObject(filter);
      }),
  ];
};

type Props = {
  initialRecommendationSetting;
  id;
};

const RuleBuilder = ({ initialRecommendationSetting, id }: Props) => {
  const history = useHistory();
  const showDebug = false;

  const salesforceUserTags = useSelector(state => state.organization.salesforce_user_tags);
  const [recommendationSetting, setRecommendationSetting] = useState<RecommendationSetting>(
    initialRecommendationSetting
  );
  const setName = name => setRecommendationSetting({ ...recommendationSetting, name });
  const setDescription = description => {
    setRecommendationSetting({
      ...recommendationSetting,
      data: recommendationSetting.data.map(row => {
        if (row.key === 'description') {
          return { ...row, value: description };
        }
        return row;
      }),
    });
  };

  const setTagsWhiteList = value => {
    setRecommendationSetting({
      ...recommendationSetting,
      data: recommendationSetting.data.map(row => {
        if (row.key === 'tags_whitelist') {
          return { ...row, value };
        }
        return row;
      }),
    });
  };

  const setCustomRuleRepresentation = value => {
    setRecommendationSetting({
      ...recommendationSetting,
      data: recommendationSetting.data.map(row => {
        if (row.key === 'custom_rule_representation') {
          return { ...row, value };
        }
        if (row.key === 'custom_rule') {
          return { ...row, value: filtersToExpression({ filters: value }) };
        }

        return row;
      }),
    });
  };

  const rule = recommendationSetting.data.find(o => o.key === 'custom_rule_representation').value;

  const addFilter = (parentUuid = null) => {
    const joinType = rule.find(f => f.parentUuid === parentUuid)?.joinType;
    setCustomRuleRepresentation([...rule, emptyFilter({ parentUuid, joinType })]);
  };
  const removeFilter = uuid => {
    const firstChild = rule.find(f => f.parentUuid === uuid);

    const newRuleRepresentation = rule
      .map(f => {
        if (f.uuid === uuid) {
          if (firstChild) return { ...firstChild, joinType: f.joinType, parentUuid: f.parentUuid };
          return null;
        }

        if (f.uuid === firstChild?.uuid) {
          return null;
        }

        if (f.parentUuid === uuid) {
          return { ...f, parentUuid: firstChild.uuid };
        }

        return f;
      })
      .filter(f => f);

    setCustomRuleRepresentation(newRuleRepresentation);
  };

  const updateFilter = (filterToUpdate, filterMetadata) => {
    const isValueRequired = !(
      filterToUpdate.operator === 'IsTrue' ||
      filterToUpdate.operator === 'IsFalse' ||
      filterToUpdate.operator === 'IsNull' ||
      filterToUpdate.operator === 'IsNotNull' ||
      filterMetadata?.type === 'boolean'
    );
    const incomplete = !(
      filterToUpdate.property &&
      filterToUpdate.operator &&
      (filterToUpdate.value || !isValueRequired)
    );

    setCustomRuleRepresentation(
      rule.map(filter => {
        if (filter.uuid === filterToUpdate.uuid) {
          return { ...filter, ...{ ...filterToUpdate, incomplete } };
        }
        return filter;
      })
    );
  };

  const toggleJoinType = parentUuid => {
    setCustomRuleRepresentation(
      rule.map(filter => {
        if (filter.parentUuid === parentUuid) {
          return { ...filter, joinType: filter.joinType === 'OR' ? 'AND' : 'OR' };
        }
        return filter;
      })
    );
  };

  // const [selectedButtonIds, setSelectedButtonIds] = useState([]);
  const [buttonOptions, setButtonOptions] = useState([]);

  const selectedButtonIds = recommendationSetting.data.find(o => o.key === 'recommendation_button_settings_ids').value;
  const setSelectedButtonIds = value => {
    return setRecommendationSetting({
      ...recommendationSetting,
      data: recommendationSetting.data.map(row => {
        if (row.key === 'recommendation_button_settings_ids') {
          return { ...row, value };
        }
        return row;
      }),
    });
  };

  const [propertyOptions, setPropertyOptions] = useState(null);
  const [messagePropertyOptions, setMessagePropertyOptions] = useState(null);

  const rootObjectRecordType = recommendationSetting.data.find(o => o.key === 'record_type').value;
  const setRootObjectRecordType = value => {
    setRecommendationSetting({
      ...recommendationSetting,
      data: recommendationSetting.data.map(row => {
        if (row.key === 'record_type') {
          return { ...row, value };
        }
        if (row.key === 'recommendation_button_settings_ids') {
          return { ...row, value: [] };
        }
        return row;
      }),
    });
  };

  const messageTemplate = recommendationSetting.data.find(o => o.key === 'message_template').value;
  const setMessageTemplate = value => {
    setRecommendationSetting({
      ...recommendationSetting,
      data: recommendationSetting.data.map(row => {
        if (row.key === 'message_template') {
          return { ...row, value };
        }
        return row;
      }),
    });
  };

  useEffect(() => {
    api.getRuleBuilderButtonOptions({ object: rootObjectRecordType }).then(response => {
      setButtonOptions(response.data);
    });

    api.getRuleBuilderFields({ object: rootObjectRecordType }).then(response => {
      const optionsData = [];
      Object.keys(response.data).forEach(key => {
        optionsData.push({
          label: key,
          options: response.data[key].map(option => ({
            ...option,
            value: `${key}.${option.name}`,
          })),
        });
      });

      const ruleBuilderOptions = optionsData.map(object => ({
        ...object,
        options: object.options.filter(option => option.allow_in_rule),
      }));
      const messageBuilderOptions = optionsData.map(object => ({
        ...object,
        options: object.options.filter(option => option.allow_in_message),
      }));
      setPropertyOptions(ruleBuilderOptions);
      setMessagePropertyOptions(messageBuilderOptions);
    });
  }, [rootObjectRecordType]);

  const filtersToDisplay = rule.filter(f => !f.parentUuid);
  const invalidRule = rule.length === 0 || rule.filter(f => f.incomplete).length > 0;

  const [saving, setSaving] = useState(false);
  const saveRule = async () => {
    setSaving(true);

    if (id) {
      await api.updateCustomRecommendationSetting(recommendationSetting);
      history.push('/assistants/sales');
    } else {
      try {
        await api.createCustomRecommendationSetting(recommendationSetting);
        history.push('/assistants/sales');
      } catch (e) {
        console.log(e.response.data);
      }
    }
    setSaving(false);
  };

  if (!propertyOptions) {
    return (
      <div className="rule-builder-container">
        <Spinner activeIf />
      </div>
    );
  }

  return (
    <WpPageLayout width="full" className="rule-builder-container">
      <TopNavigation />
      <WpPageBody>
        <div className="border-bottom pb-2 mb-4 ">
          <div className="form-group">
            <label>Rule Name</label>
            <input
              className="form-control"
              placeholder="Name your custom rule"
              type="text"
              value={recommendationSetting.name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-control"
              placeholder="Describe your custom rule"
              onChange={e => setDescription(e.target.value)}
              value={recommendationSetting.data.find(o => o.key === 'description').value}
            />
          </div>

          <div className="form-group">
            <label>Only send to users with these tags:</label>
            <Select
              className="property mr-2"
              placeholder="Tags"
              options={salesforceUserTags}
              onChange={e => setTagsWhiteList(e || [])}
              value={recommendationSetting.data.find(o => o.key === 'tags_whitelist').value}
              isMulti
            />
          </div>
        </div>
        <div className="mb-4">
          <div className="d-flex mt-3 align-items-center">
            <div className="mr-2">Filter on</div>
            <div style={{ width: '160px' }}>
              <Select
                className="property mr-2"
                placeholder="Root Object Record Type"
                options={[
                  { value: 'Opportunity', label: 'Opportunity' },
                  { value: 'Lead', label: 'Lead' },
                  { value: 'Contact', label: 'Contact' },
                  { value: 'Account', label: 'Account' },
                ]}
                value={{ value: rootObjectRecordType, label: rootObjectRecordType }}
                onChange={p => {
                  setRootObjectRecordType(p.value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="mr-2">Filters:</div>
        {filtersToDisplay.map((filter, index) => (
          <Filter
            propertyOptions={propertyOptions}
            onAddFilter={addFilter}
            onRemoveFilter={removeFilter}
            filter={filter}
            childFilters={rule.filter(f => f.parentUuid === filter.uuid)}
            uuid={filter.uuid}
            parentUuid={filter.parentUuid}
            key={filter.uuid}
            isFirst={index === 0}
            onChange={updateFilter}
            joinType={filter.joinType}
            toggleJoinType={toggleJoinType}
          />
        ))}
        <div className="d-flex mt-3 align-items-center">
          <WpButton variant="secondary" onClick={() => addFilter()}>
            <WpIcon name="plus-circle" />
            Add Filter
          </WpButton>

          <div className="mx-3 divider">|</div>

          <div className="d-flex align-items-center toggle-join" onClick={() => toggleJoinType(null)}>
            AND
            <span className="mx-2">
              <WpIcon name={rule[0]?.joinType === 'OR' ? 'toggle-on' : 'toggle-off'} />
            </span>
            OR
          </div>
        </div>
        {showDebug && (
          <div className="mt-4 p-2" style={{ backgroundColor: '#eee' }}>
            <code>{JSON.stringify(rule)}</code>
          </div>
        )}
        {showDebug && (
          <div className="mt-4 p-2" style={{ backgroundColor: '#eee' }}>
            <code>{JSON.stringify(filtersToExpression({ filters: rule }))}</code>
          </div>
        )}
        {showDebug && (
          <div className="mt-4 p-2" style={{ backgroundColor: '#eee' }}>
            <code>{JSON.stringify(recommendationSetting)}</code>
          </div>
        )}
        <h4 className="mr-2 mt-4">Message:</h4>
        <MessageTemplateBuilder
          propertyOptions={messagePropertyOptions}
          messageTemplate={messageTemplate}
          onMessageTemplateChange={setMessageTemplate}
        />
        {showDebug && (
          <div className="mt-4 p-2" style={{ backgroundColor: '#eee' }}>
            <code>{messageTemplate}</code>
          </div>
        )}
        <h4 className="mr-2 mt-4">Buttons:</h4>
        <ButtonSelector
          selectedButtonIds={selectedButtonIds}
          buttonOptions={buttonOptions}
          setSelectedButtonIds={setSelectedButtonIds}
        />
        {showDebug && (
          <div className="mt-4 p-2" style={{ backgroundColor: '#eee' }}>
            <code>{JSON.stringify(selectedButtonIds)}</code>
          </div>
        )}

        <div className="border-top pt-4 my-4">
          <button type="submit" className="btn btn-primary mr-2" onClick={saveRule} disabled={saving || invalidRule}>
            {saving ? 'Saving...' : 'Save Rule'}
          </button>
          <PreviewButton recommendationSetting={recommendationSetting} disabled={invalidRule} />
        </div>
      </WpPageBody>
    </WpPageLayout>
  );
};

export default RuleBuilder;
